import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Groups as GroupsIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { departmentService, locationService } from '../services/authService';

interface CompanySettings {
  id?: number;
  name: string;
  legal_name: string;
  industry: string;
  company_size: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  primary_regulations: string;
  safety_policies: string;
  compliance_requirements: string;
  training_context: string;
  target_audience: string;
  timezone: string;
}

const INDUSTRY_OPTIONS = [
  { value: 'construction', label: 'Construction' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'warehousing', label: 'Warehousing' },
  { value: 'other', label: 'Other' },
];

const SIZE_OPTIONS = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time - Indiana (ET)' },
  { value: 'America/Detroit', label: 'Eastern Time - Detroit (ET)' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern Time - Louisville (ET)' },
  { value: 'America/Kentucky/Monticello', label: 'Eastern Time - Monticello (ET)' },
  { value: 'America/Indiana/Vincennes', label: 'Eastern Time - Vincennes (ET)' },
  { value: 'America/Indiana/Winamac', label: 'Eastern Time - Winamac (ET)' },
  { value: 'America/Indiana/Marengo', label: 'Eastern Time - Marengo (ET)' },
  { value: 'America/Indiana/Petersburg', label: 'Eastern Time - Petersburg (ET)' },
  { value: 'America/Indiana/Vevay', label: 'Eastern Time - Vevay (ET)' },
  { value: 'America/Chicago', label: 'Central Time - Chicago (CT)' },
  { value: 'America/Indiana/Tell_City', label: 'Central Time - Tell City (CT)' },
  { value: 'America/Indiana/Knox', label: 'Central Time - Knox (CT)' },
  { value: 'America/Menominee', label: 'Central Time - Menominee (CT)' },
  { value: 'America/North_Dakota/Center', label: 'Central Time - Center (CT)' },
  { value: 'America/North_Dakota/New_Salem', label: 'Central Time - New Salem (CT)' },
  { value: 'America/North_Dakota/Beulah', label: 'Central Time - Beulah (CT)' },
  { value: 'America/Denver', label: 'Mountain Time - Denver (MT)' },
  { value: 'America/Boise', label: 'Mountain Time - Boise (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Phoenix (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time - Los Angeles (PT)' },
  { value: 'America/Metlakatla', label: 'Pacific Time - Metlakatla (PT)' },
  { value: 'America/Sitka', label: 'Alaska Time - Sitka (AKT)' },
  { value: 'America/Juneau', label: 'Alaska Time - Juneau (AKT)' },
  { value: 'America/Yakutat', label: 'Alaska Time - Yakutat (AKT)' },
  { value: 'America/Nome', label: 'Alaska Time - Nome (AKT)' },
  { value: 'America/Anchorage', label: 'Alaska Time - Anchorage (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time - Honolulu (HST)' },
  { value: 'America/Adak', label: 'Hawaii Time - Adak (HST)' },
  { value: 'Pacific/Midway', label: 'Hawaii Time - Midway (SST)' },
  { value: 'Pacific/Wake', label: 'Hawaii Time - Wake (WAKT)' },
  { value: 'Pacific/Samoa', label: 'Hawaii Time - Samoa (SST)' },
  { value: 'Pacific/Guam', label: 'Guam Time (ChST)' },
  { value: 'Pacific/Saipan', label: 'Northern Mariana Islands Time (ChST)' },
  { value: 'Pacific/Palau', label: 'Palau Time (PWT)' },
  { value: 'Pacific/Chuuk', label: 'Chuuk Time (CHUT)' },
  { value: 'Pacific/Pohnpei', label: 'Pohnpei Time (PONT)' },
  { value: 'Pacific/Kosrae', label: 'Kosrae Time (KOST)' },
  { value: 'Pacific/Majuro', label: 'Marshall Islands Time (MHT)' },
  { value: 'Pacific/Kwajalein', label: 'Marshall Islands Time - Kwajalein (MHT)' },
  { value: 'Pacific/Tarawa', label: 'Kiribati Time - Tarawa (GILT)' },
  { value: 'Pacific/Enderbury', label: 'Kiribati Time - Enderbury (PHOT)' },
  { value: 'Pacific/Kiritimati', label: 'Kiribati Time - Kiritimati (LINT)' },
  { value: 'Pacific/Fiji', label: 'Fiji Time (FJT)' },
  { value: 'Pacific/Tongatapu', label: 'Tonga Time (TOT)' },
  { value: 'Pacific/Apia', label: 'Samoa Time (WST)' },
  { value: 'Pacific/Niue', label: 'Niue Time (NUT)' },
  { value: 'Pacific/Rarotonga', label: 'Cook Islands Time (CKT)' },
  { value: 'Pacific/Tahiti', label: 'Tahiti Time (TAHT)' },
  { value: 'Pacific/Marquesas', label: 'Marquesas Time (MART)' },
  { value: 'Pacific/Gambier', label: 'Gambier Time (GAMT)' },
  { value: 'Pacific/Pitcairn', label: 'Pitcairn Time (PST)' },
  { value: 'Atlantic/Bermuda', label: 'Atlantic Time - Bermuda (AST)' },
  { value: 'Atlantic/Azores', label: 'Azores Time (AZOT)' },
  { value: 'Atlantic/Cape_Verde', label: 'Cape Verde Time (CVT)' },
  { value: 'Atlantic/Canary', label: 'Canary Islands Time (WET)' },
  { value: 'Atlantic/Madeira', label: 'Madeira Time (WET)' },
  { value: 'Atlantic/Reykjavik', label: 'Iceland Time (GMT)' },
  { value: 'Atlantic/St_Helena', label: 'St. Helena Time (GMT)' },
  { value: 'Atlantic/Stanley', label: 'Falkland Islands Time (FKT)' },
  { value: 'Atlantic/South_Georgia', label: 'South Georgia Time (GST)' },
  { value: 'Atlantic/Faroe', label: 'Faroe Islands Time (WET)' },
  { value: 'Atlantic/Jan_Mayen', label: 'Jan Mayen Time (CET)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Dublin', label: 'Irish Standard Time (IST)' },
  { value: 'Europe/Lisbon', label: 'Western European Time (WET)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time - Berlin (CET)' },
  { value: 'Europe/Rome', label: 'Central European Time - Rome (CET)' },
  { value: 'Europe/Madrid', label: 'Central European Time - Madrid (CET)' },
  { value: 'Europe/Amsterdam', label: 'Central European Time - Amsterdam (CET)' },
  { value: 'Europe/Brussels', label: 'Central European Time - Brussels (CET)' },
  { value: 'Europe/Vienna', label: 'Central European Time - Vienna (CET)' },
  { value: 'Europe/Zurich', label: 'Central European Time - Zurich (CET)' },
  { value: 'Europe/Stockholm', label: 'Central European Time - Stockholm (CET)' },
  { value: 'Europe/Oslo', label: 'Central European Time - Oslo (CET)' },
  { value: 'Europe/Copenhagen', label: 'Central European Time - Copenhagen (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time - Helsinki (EET)' },
  { value: 'Europe/Warsaw', label: 'Central European Time - Warsaw (CET)' },
  { value: 'Europe/Prague', label: 'Central European Time - Prague (CET)' },
  { value: 'Europe/Budapest', label: 'Central European Time - Budapest (CET)' },
  { value: 'Europe/Bucharest', label: 'Eastern European Time - Bucharest (EET)' },
  { value: 'Europe/Sofia', label: 'Eastern European Time - Sofia (EET)' },
  { value: 'Europe/Athens', label: 'Eastern European Time - Athens (EET)' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (TRT)' },
  { value: 'Europe/Moscow', label: 'Moscow Time (MSK)' },
  { value: 'Europe/Kiev', label: 'Eastern European Time - Kiev (EET)' },
  { value: 'Europe/Minsk', label: 'Moscow Time - Minsk (MSK)' },
  { value: 'Europe/Riga', label: 'Eastern European Time - Riga (EET)' },
  { value: 'Europe/Tallinn', label: 'Eastern European Time - Tallinn (EET)' },
  { value: 'Europe/Vilnius', label: 'Eastern European Time - Vilnius (EET)' },
  { value: 'Europe/Kaliningrad', label: 'Kaliningrad Time (EET)' },
  { value: 'Europe/Volgograd', label: 'Volgograd Time (MSK)' },
  { value: 'Europe/Samara', label: 'Samara Time (SAMT)' },
  { value: 'Europe/Yekaterinburg', label: 'Yekaterinburg Time (YEKT)' },
  { value: 'Europe/Omsk', label: 'Omsk Time (OMST)' },
  { value: 'Europe/Novosibirsk', label: 'Novosibirsk Time (NOVT)' },
  { value: 'Europe/Krasnoyarsk', label: 'Krasnoyarsk Time (KRAT)' },
  { value: 'Europe/Irkutsk', label: 'Irkutsk Time (IRKT)' },
  { value: 'Europe/Yakutsk', label: 'Yakutsk Time (YAKT)' },
  { value: 'Europe/Vladivostok', label: 'Vladivostok Time (VLAT)' },
  { value: 'Asia/Magadan', label: 'Magadan Time (MAGT)' },
  { value: 'Asia/Kamchatka', label: 'Kamchatka Time (PETT)' },
  { value: 'Asia/Anadyr', label: 'Anadyr Time (ANAT)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Asia/Muscat', label: 'Gulf Standard Time - Muscat (GST)' },
  { value: 'Asia/Baku', label: 'Azerbaijan Time (AZT)' },
  { value: 'Asia/Tbilisi', label: 'Georgia Time (GET)' },
  { value: 'Asia/Yerevan', label: 'Armenia Time (AMT)' },
  { value: 'Asia/Tehran', label: 'Iran Standard Time (IRST)' },
  { value: 'Asia/Kabul', label: 'Afghanistan Time (AFT)' },
  { value: 'Asia/Karachi', label: 'Pakistan Standard Time (PKT)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST)' },
  { value: 'Asia/Colombo', label: 'Sri Lanka Time (LKT)' },
  { value: 'Asia/Kathmandu', label: 'Nepal Time (NPT)' },
  { value: 'Asia/Thimphu', label: 'Bhutan Time (BTT)' },
  { value: 'Asia/Yangon', label: 'Myanmar Time (MMT)' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (ICT)' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (WIB)' },
  { value: 'Asia/Pontianak', label: 'Western Indonesia Time - Pontianak (WIB)' },
  { value: 'Asia/Makassar', label: 'Central Indonesia Time (WITA)' },
  { value: 'Asia/Jayapura', label: 'Eastern Indonesia Time (WIT)' },
  { value: 'Asia/Manila', label: 'Philippine Time (PHT)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time (MYT)' },
  { value: 'Asia/Kuching', label: 'Malaysia Time - Kuching (MYT)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Brunei', label: 'Brunei Time (BNT)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT)' },
  { value: 'Asia/Macau', label: 'Macau Time (CST)' },
  { value: 'Asia/Taipei', label: 'Taiwan Time (CST)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (KST)' },
  { value: 'Asia/Pyongyang', label: 'North Korea Time (KST)' },
  { value: 'Asia/Ulaanbaatar', label: 'Ulaanbaatar Time (ULAT)' },
  { value: 'Asia/Hovd', label: 'Hovd Time (HOVT)' },
  { value: 'Asia/Choibalsan', label: 'Choibalsan Time (CHOT)' },
  { value: 'Asia/Vladivostok', label: 'Vladivostok Time (VLAT)' },
  { value: 'Asia/Sakhalin', label: 'Sakhalin Time (SAKT)' },
  { value: 'Asia/Magadan', label: 'Magadan Time (MAGT)' },
  { value: 'Asia/Kamchatka', label: 'Kamchatka Time (PETT)' },
  { value: 'Asia/Anadyr', label: 'Anadyr Time (ANAT)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWST)' },
  { value: 'Australia/Eucla', label: 'Australian Central Western Time (ACWST)' },
  { value: 'Australia/Darwin', label: 'Australian Central Time - Darwin (ACST)' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time - Adelaide (ACST)' },
  { value: 'Australia/Broken_Hill', label: 'Australian Central Time - Broken Hill (ACST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time - Sydney (AEST)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time - Melbourne (AEST)' },
  { value: 'Australia/Hobart', label: 'Australian Eastern Time - Hobart (AEST)' },
  { value: 'Australia/Lord_Howe', label: 'Lord Howe Time (LHST)' },
  { value: 'Pacific/Norfolk', label: 'Norfolk Island Time (NFT)' },
  { value: 'Pacific/Chatham', label: 'Chatham Time (CHAST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (NZST)' },
  { value: 'Pacific/Port_Moresby', label: 'Papua New Guinea Time (PGT)' },
  { value: 'Pacific/Bougainville', label: 'Bougainville Time (BST)' },
  { value: 'Pacific/Guadalcanal', label: 'Solomon Islands Time (SBT)' },
  { value: 'Pacific/Efate', label: 'Vanuatu Time (VUT)' },
  { value: 'Pacific/Noumea', label: 'New Caledonia Time (NCT)' },
  { value: 'Pacific/Wallis', label: 'Wallis and Futuna Time (WFT)' },
  { value: 'Pacific/Funafuti', label: 'Tuvalu Time (TVT)' },
  { value: 'Pacific/Tarawa', label: 'Kiribati Time - Tarawa (GILT)' },
  { value: 'Pacific/Enderbury', label: 'Kiribati Time - Enderbury (PHOT)' },
  { value: 'Pacific/Kiritimati', label: 'Kiribati Time - Kiritimati (LINT)' },
  { value: 'Pacific/Fiji', label: 'Fiji Time (FJT)' },
  { value: 'Pacific/Tongatapu', label: 'Tonga Time (TOT)' },
  { value: 'Pacific/Apia', label: 'Samoa Time (WST)' },
  { value: 'Pacific/Niue', label: 'Niue Time (NUT)' },
  { value: 'Pacific/Rarotonga', label: 'Cook Islands Time (CKT)' },
  { value: 'Pacific/Tahiti', label: 'Tahiti Time (TAHT)' },
  { value: 'Pacific/Marquesas', label: 'Marquesas Time (MART)' },
  { value: 'Pacific/Gambier', label: 'Gambier Time (GAMT)' },
  { value: 'Pacific/Pitcairn', label: 'Pitcairn Time (PST)' },
  { value: 'America/New_York', label: 'Eastern Time - New York (ET)' },
  { value: 'America/Detroit', label: 'Eastern Time - Detroit (ET)' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern Time - Louisville (ET)' },
  { value: 'America/Kentucky/Monticello', label: 'Eastern Time - Monticello (ET)' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time - Indianapolis (ET)' },
  { value: 'America/Indiana/Vincennes', label: 'Eastern Time - Vincennes (ET)' },
  { value: 'America/Indiana/Winamac', label: 'Eastern Time - Winamac (ET)' },
  { value: 'America/Indiana/Marengo', label: 'Eastern Time - Marengo (ET)' },
  { value: 'America/Indiana/Petersburg', label: 'Eastern Time - Petersburg (ET)' },
  { value: 'America/Indiana/Vevay', label: 'Eastern Time - Vevay (ET)' },
  { value: 'America/Indiana/Tell_City', label: 'Central Time - Tell City (CT)' },
  { value: 'America/Indiana/Knox', label: 'Central Time - Knox (CT)' },
  { value: 'America/Menominee', label: 'Central Time - Menominee (CT)' },
  { value: 'America/North_Dakota/Center', label: 'Central Time - Center (CT)' },
  { value: 'America/North_Dakota/New_Salem', label: 'Central Time - New Salem (CT)' },
  { value: 'America/North_Dakota/Beulah', label: 'Central Time - Beulah (CT)' },
  { value: 'America/Boise', label: 'Mountain Time - Boise (MT)' },
  { value: 'America/Metlakatla', label: 'Pacific Time - Metlakatla (PT)' },
  { value: 'America/Sitka', label: 'Alaska Time - Sitka (AKT)' },
  { value: 'America/Juneau', label: 'Alaska Time - Juneau (AKT)' },
  { value: 'America/Yakutat', label: 'Alaska Time - Yakutat (AKT)' },
  { value: 'America/Nome', label: 'Alaska Time - Nome (AKT)' },
  { value: 'America/Adak', label: 'Hawaii Time - Adak (HST)' },
  { value: 'Pacific/Midway', label: 'Hawaii Time - Midway (SST)' },
  { value: 'Pacific/Wake', label: 'Hawaii Time - Wake (WAKT)' },
  { value: 'Pacific/Samoa', label: 'Hawaii Time - Samoa (SST)' },
  { value: 'Atlantic/Bermuda', label: 'Atlantic Time - Bermuda (AST)' },
  { value: 'Atlantic/Azores', label: 'Azores Time (AZOT)' },
  { value: 'Atlantic/Cape_Verde', label: 'Cape Verde Time (CVT)' },
  { value: 'Atlantic/Canary', label: 'Canary Islands Time (WET)' },
  { value: 'Atlantic/Madeira', label: 'Madeira Time (WET)' },
  { value: 'Atlantic/Reykjavik', label: 'Iceland Time (GMT)' },
  { value: 'Atlantic/St_Helena', label: 'St. Helena Time (GMT)' },
  { value: 'Atlantic/Stanley', label: 'Falkland Islands Time (FKT)' },
  { value: 'Atlantic/South_Georgia', label: 'South Georgia Time (GST)' },
  { value: 'Atlantic/Faroe', label: 'Faroe Islands Time (WET)' },
  { value: 'Atlantic/Jan_Mayen', label: 'Jan Mayen Time (CET)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Dublin', label: 'Irish Standard Time (IST)' },
  { value: 'Europe/Lisbon', label: 'Western European Time (WET)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time - Berlin (CET)' },
  { value: 'Europe/Rome', label: 'Central European Time - Rome (CET)' },
  { value: 'Europe/Madrid', label: 'Central European Time - Madrid (CET)' },
  { value: 'Europe/Amsterdam', label: 'Central European Time - Amsterdam (CET)' },
  { value: 'Europe/Brussels', label: 'Central European Time - Brussels (CET)' },
  { value: 'Europe/Vienna', label: 'Central European Time - Vienna (CET)' },
  { value: 'Europe/Zurich', label: 'Central European Time - Zurich (CET)' },
  { value: 'Europe/Stockholm', label: 'Central European Time - Stockholm (CET)' },
  { value: 'Europe/Oslo', label: 'Central European Time - Oslo (CET)' },
  { value: 'Europe/Copenhagen', label: 'Central European Time - Copenhagen (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern European Time - Helsinki (EET)' },
  { value: 'Europe/Warsaw', label: 'Central European Time - Warsaw (CET)' },
  { value: 'Europe/Prague', label: 'Central European Time - Prague (CET)' },
  { value: 'Europe/Budapest', label: 'Central European Time - Budapest (CET)' },
  { value: 'Europe/Bucharest', label: 'Eastern European Time - Bucharest (EET)' },
  { value: 'Europe/Sofia', label: 'Eastern European Time - Sofia (EET)' },
  { value: 'Europe/Athens', label: 'Eastern European Time - Athens (EET)' },
  { value: 'Europe/Istanbul', label: 'Turkey Time (TRT)' },
  { value: 'Europe/Moscow', label: 'Moscow Time (MSK)' },
  { value: 'Europe/Kiev', label: 'Eastern European Time - Kiev (EET)' },
  { value: 'Europe/Minsk', label: 'Moscow Time - Minsk (MSK)' },
  { value: 'Europe/Riga', label: 'Eastern European Time - Riga (EET)' },
  { value: 'Europe/Tallinn', label: 'Eastern European Time - Tallinn (EET)' },
  { value: 'Europe/Vilnius', label: 'Eastern European Time - Vilnius (EET)' },
  { value: 'Europe/Kaliningrad', label: 'Kaliningrad Time (EET)' },
  { value: 'Europe/Volgograd', label: 'Volgograd Time (MSK)' },
  { value: 'Europe/Samara', label: 'Samara Time (SAMT)' },
  { value: 'Europe/Yekaterinburg', label: 'Yekaterinburg Time (YEKT)' },
  { value: 'Europe/Omsk', label: 'Omsk Time (OMST)' },
  { value: 'Europe/Novosibirsk', label: 'Novosibirsk Time (NOVT)' },
  { value: 'Europe/Krasnoyarsk', label: 'Krasnoyarsk Time (KRAT)' },
  { value: 'Europe/Irkutsk', label: 'Irkutsk Time (IRKT)' },
  { value: 'Europe/Yakutsk', label: 'Yakutsk Time (YAKT)' },
  { value: 'Europe/Vladivostok', label: 'Vladivostok Time (VLAT)' },
  { value: 'Asia/Magadan', label: 'Magadan Time (MAGT)' },
  { value: 'Asia/Kamchatka', label: 'Kamchatka Time (PETT)' },
  { value: 'Asia/Anadyr', label: 'Anadyr Time (ANAT)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Asia/Muscat', label: 'Gulf Standard Time - Muscat (GST)' },
  { value: 'Asia/Baku', label: 'Azerbaijan Time (AZT)' },
  { value: 'Asia/Tbilisi', label: 'Georgia Time (GET)' },
  { value: 'Asia/Yerevan', label: 'Armenia Time (AMT)' },
  { value: 'Asia/Tehran', label: 'Iran Standard Time (IRST)' },
  { value: 'Asia/Kabul', label: 'Afghanistan Time (AFT)' },
  { value: 'Asia/Karachi', label: 'Pakistan Standard Time (PKT)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST)' },
  { value: 'Asia/Colombo', label: 'Sri Lanka Time (LKT)' },
  { value: 'Asia/Kathmandu', label: 'Nepal Time (NPT)' },
  { value: 'Asia/Thimphu', label: 'Bhutan Time (BTT)' },
  { value: 'Asia/Yangon', label: 'Myanmar Time (MMT)' },
  { value: 'Asia/Bangkok', label: 'Indochina Time (ICT)' },
  { value: 'Asia/Jakarta', label: 'Western Indonesia Time (WIB)' },
  { value: 'Asia/Pontianak', label: 'Western Indonesia Time - Pontianak (WIB)' },
  { value: 'Asia/Makassar', label: 'Central Indonesia Time (WITA)' },
  { value: 'Asia/Jayapura', label: 'Eastern Indonesia Time (WIT)' },
  { value: 'Asia/Manila', label: 'Philippine Time (PHT)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia Time (MYT)' },
  { value: 'Asia/Kuching', label: 'Malaysia Time - Kuching (MYT)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Brunei', label: 'Brunei Time (BNT)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong Time (HKT)' },
  { value: 'Asia/Macau', label: 'Macau Time (CST)' },
  { value: 'Asia/Taipei', label: 'Taiwan Time (CST)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Seoul', label: 'Korea Standard Time (KST)' },
  { value: 'Asia/Pyongyang', label: 'North Korea Time (KST)' },
  { value: 'Asia/Ulaanbaatar', label: 'Ulaanbaatar Time (ULAT)' },
  { value: 'Asia/Hovd', label: 'Hovd Time (HOVT)' },
  { value: 'Asia/Choibalsan', label: 'Choibalsan Time (CHOT)' },
  { value: 'Asia/Vladivostok', label: 'Vladivostok Time (VLAT)' },
  { value: 'Asia/Sakhalin', label: 'Sakhalin Time (SAKT)' },
  { value: 'Asia/Magadan', label: 'Magadan Time (MAGT)' },
  { value: 'Asia/Kamchatka', label: 'Kamchatka Time (PETT)' },
  { value: 'Asia/Anadyr', label: 'Anadyr Time (ANAT)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWST)' },
  { value: 'Australia/Eucla', label: 'Australian Central Western Time (ACWST)' },
  { value: 'Australia/Darwin', label: 'Australian Central Time - Darwin (ACST)' },
  { value: 'Australia/Adelaide', label: 'Australian Central Time - Adelaide (ACST)' },
  { value: 'Australia/Broken_Hill', label: 'Australian Central Time - Broken Hill (ACST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time - Sydney (AEST)' },
  { value: 'Australia/Melbourne', label: 'Australian Eastern Time - Melbourne (AEST)' },
  { value: 'Australia/Hobart', label: 'Australian Eastern Time - Hobart (AEST)' },
  { value: 'Australia/Lord_Howe', label: 'Lord Howe Time (LHST)' },
  { value: 'Pacific/Norfolk', label: 'Norfolk Island Time (NFT)' },
  { value: 'Pacific/Chatham', label: 'Chatham Time (CHAST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (NZST)' },
  { value: 'Pacific/Port_Moresby', label: 'Papua New Guinea Time (PGT)' },
  { value: 'Pacific/Bougainville', label: 'Bougainville Time (BST)' },
  { value: 'Pacific/Guadalcanal', label: 'Solomon Islands Time (SBT)' },
  { value: 'Pacific/Efate', label: 'Vanuatu Time (VUT)' },
  { value: 'Pacific/Noumea', label: 'New Caledonia Time (NCT)' },
  { value: 'Pacific/Wallis', label: 'Wallis and Futuna Time (WFT)' },
  { value: 'Pacific/Funafuti', label: 'Tuvalu Time (TVT)' },
  { value: 'Pacific/Tarawa', label: 'Kiribati Time - Tarawa (GILT)' },
  { value: 'Pacific/Enderbury', label: 'Kiribati Time - Enderbury (PHOT)' },
  { value: 'Pacific/Kiritimati', label: 'Kiribati Time - Kiritimati (LINT)' },
  { value: 'Pacific/Fiji', label: 'Fiji Time (FJT)' },
  { value: 'Pacific/Tongatapu', label: 'Tonga Time (TOT)' },
  { value: 'Pacific/Apia', label: 'Samoa Time (WST)' },
  { value: 'Pacific/Niue', label: 'Niue Time (NUT)' },
  { value: 'Pacific/Rarotonga', label: 'Cook Islands Time (CKT)' },
  { value: 'Pacific/Tahiti', label: 'Tahiti Time (TAHT)' },
  { value: 'Pacific/Marquesas', label: 'Marquesas Time (MART)' },
  { value: 'Pacific/Gambier', label: 'Gambier Time (GAMT)' },
  { value: 'Pacific/Pitcairn', label: 'Pitcairn Time (PST)' },
];

// Deduplicate any accidental duplicates by value to avoid React key warnings
const TIMEZONE_OPTIONS_DEDUPED = Array.from(
  new Map(TIMEZONE_OPTIONS.map((opt) => [opt.value, opt])).values()
);

interface Department {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface Location {
  id: number;
  name: string;
  address: string;
  is_active: boolean;
}

interface DocumentCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
}

interface TrainingCategory {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
}

const CompanySettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<CompanySettings>({
    name: '',
    legal_name: '',
    industry: '',
    company_size: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: '',
    email: '',
    website: '',
    primary_regulations: '',
    safety_policies: '',
    compliance_requirements: '',
    training_context: '',
    target_audience: '',
    timezone: 'America/Los_Angeles',
  });

  // Departments state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [departmentForm, setDepartmentForm] = useState({ name: '', description: '', is_active: true });

  // Locations state
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [locationForm, setLocationForm] = useState({ name: '', address: '', is_active: true });

  // Document Categories state
  const [documentCategories, setDocumentCategories] = useState<DocumentCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DocumentCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', icon: 'description', color: '#1976d2', is_active: true });

  // Training Categories state
  const [trainingCategories, setTrainingCategories] = useState<TrainingCategory[]>([]);
  const [trainingCategoriesLoading, setTrainingCategoriesLoading] = useState(false);
  const [trainingCategoryDialogOpen, setTrainingCategoryDialogOpen] = useState(false);
  const [editingTrainingCategory, setEditingTrainingCategory] = useState<TrainingCategory | null>(null);
  const [trainingCategoryForm, setTrainingCategoryForm] = useState({ name: '', description: '', icon: 'school', color: '#1976d2', is_active: true });

  useEffect(() => {
    fetchCompanySettings();
    fetchDepartments();
    fetchLocations();
    fetchDocumentCategories();
    fetchTrainingCategories();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/companies/current/');
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching company settings:', error);
      setError('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CompanySettings) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Remove undefined fields
      const submitData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== undefined)
      );

      const response = await api.post('/companies/', submitData);
      setSuccess('Company settings saved successfully!');
    } catch (error: any) {
      console.error('Error saving company settings:', error);
      setError(error.response?.data?.error || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  // Departments functions
  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const depts = await departmentService.getAll();
      setDepartments(depts);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const handleDepartmentDialogOpen = (dept?: Department) => {
    if (dept) {
      setEditingDepartment(dept);
      setDepartmentForm({ name: dept.name, description: dept.description, is_active: dept.is_active });
    } else {
      setEditingDepartment(null);
      setDepartmentForm({ name: '', description: '', is_active: true });
    }
    setDepartmentDialogOpen(true);
  };

  const handleDepartmentDialogClose = () => {
    setDepartmentDialogOpen(false);
    setEditingDepartment(null);
    setDepartmentForm({ name: '', description: '', is_active: true });
  };

  const handleDepartmentSave = async () => {
    try {
      if (editingDepartment) {
        // Update
        await api.patch(`/departments/${editingDepartment.id}/`, departmentForm);
        setSuccess('Department updated successfully!');
      } else {
        // Create
        await departmentService.create(departmentForm);
        setSuccess('Department created successfully!');
      }
      await fetchDepartments();
      handleDepartmentDialogClose();
    } catch (error: any) {
      console.error('Error saving department:', error);
      setError(error.response?.data?.detail || 'Failed to save department');
    }
  };

  const handleDepartmentDelete = async (dept: Department) => {
    if (!window.confirm(`Are you sure you want to delete "${dept.name}"?`)) {
      return;
    }
    try {
      await api.delete(`/departments/${dept.id}/`);
      setSuccess('Department deleted successfully!');
      await fetchDepartments();
    } catch (error: any) {
      console.error('Error deleting department:', error);
      setError(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  // Locations functions
  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);
      const locs = await locationService.getAll();
      setLocations(locs);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  };

  const handleLocationDialogOpen = (loc?: Location) => {
    if (loc) {
      setEditingLocation(loc);
      setLocationForm({ name: loc.name, address: loc.address, is_active: loc.is_active });
    } else {
      setEditingLocation(null);
      setLocationForm({ name: '', address: '', is_active: true });
    }
    setLocationDialogOpen(true);
  };

  const handleLocationDialogClose = () => {
    setLocationDialogOpen(false);
    setEditingLocation(null);
    setLocationForm({ name: '', address: '', is_active: true });
  };

  const handleLocationSave = async () => {
    try {
      if (editingLocation) {
        // Update
        await api.patch(`/locations/${editingLocation.id}/`, locationForm);
        setSuccess('Location updated successfully!');
      } else {
        // Create
        await locationService.create(locationForm);
        setSuccess('Location created successfully!');
      }
      await fetchLocations();
      handleLocationDialogClose();
    } catch (error: any) {
      console.error('Error saving location:', error);
      setError(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleLocationDelete = async (loc: Location) => {
    if (!window.confirm(`Are you sure you want to delete "${loc.name}"?`)) {
      return;
    }
    try {
      await api.delete(`/locations/${loc.id}/`);
      setSuccess('Location deleted successfully!');
      await fetchLocations();
    } catch (error: any) {
      console.error('Error deleting location:', error);
      setError(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  // Document Categories functions
  const fetchDocumentCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await api.get('/document-categories/');
      const data = response.data.results || response.data;
      setDocumentCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching document categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleCategoryDialogOpen = (cat?: DocumentCategory) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({ name: cat.name, description: cat.description, icon: cat.icon || 'description', color: cat.color || '#1976d2', is_active: cat.is_active });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', icon: 'description', color: '#1976d2', is_active: true });
    }
    setCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', icon: 'description', color: '#1976d2', is_active: true });
  };

  const handleCategorySave = async () => {
    try {
      if (editingCategory) {
        // Update
        await api.patch(`/document-categories/${editingCategory.id}/`, categoryForm);
        setSuccess('Document category updated successfully!');
      } else {
        // Create
        await api.post('/document-categories/', categoryForm);
        setSuccess('Document category created successfully!');
      }
      await fetchDocumentCategories();
      handleCategoryDialogClose();
    } catch (error: any) {
      console.error('Error saving document category:', error);
      setError(error.response?.data?.detail || 'Failed to save document category');
    }
  };

  const handleCategoryDelete = async (cat: DocumentCategory) => {
    if (!window.confirm(`Are you sure you want to delete "${cat.name}"? This may affect documents using this category.`)) {
      return;
    }
    try {
      await api.delete(`/document-categories/${cat.id}/`);
      setSuccess('Document category deleted successfully!');
      await fetchDocumentCategories();
    } catch (error: any) {
      console.error('Error deleting document category:', error);
      setError(error.response?.data?.detail || 'Failed to delete document category');
    }
  };

  // Training Categories functions
  const fetchTrainingCategories = async () => {
    try {
      setTrainingCategoriesLoading(true);
      const response = await api.get('/training-categories/');
      const data = response.data.results || response.data;
      setTrainingCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching training categories:', error);
    } finally {
      setTrainingCategoriesLoading(false);
    }
  };

  const handleTrainingCategoryDialogOpen = (cat?: TrainingCategory) => {
    if (cat) {
      setEditingTrainingCategory(cat);
      setTrainingCategoryForm({ name: cat.name, description: cat.description, icon: cat.icon || 'school', color: cat.color || '#1976d2', is_active: cat.is_active });
    } else {
      setEditingTrainingCategory(null);
      setTrainingCategoryForm({ name: '', description: '', icon: 'school', color: '#1976d2', is_active: true });
    }
    setTrainingCategoryDialogOpen(true);
  };

  const handleTrainingCategoryDialogClose = () => {
    setTrainingCategoryDialogOpen(false);
    setEditingTrainingCategory(null);
    setTrainingCategoryForm({ name: '', description: '', icon: 'school', color: '#1976d2', is_active: true });
  };

  const handleTrainingCategorySave = async () => {
    try {
      if (editingTrainingCategory) {
        // Update
        await api.patch(`/training-categories/${editingTrainingCategory.id}/`, trainingCategoryForm);
        setSuccess('Training category updated successfully!');
      } else {
        // Create
        await api.post('/training-categories/', trainingCategoryForm);
        setSuccess('Training category created successfully!');
      }
      await fetchTrainingCategories();
      handleTrainingCategoryDialogClose();
    } catch (error: any) {
      console.error('Error saving training category:', error);
      setError(error.response?.data?.detail || 'Failed to save training category');
    }
  };

  const handleTrainingCategoryDelete = async (cat: TrainingCategory) => {
    if (!window.confirm(`Are you sure you want to delete "${cat.name}"? This may affect trainings using this category.`)) {
      return;
    }
    try {
      await api.delete(`/training-categories/${cat.id}/`);
      setSuccess('Training category deleted successfully!');
      await fetchTrainingCategories();
    } catch (error: any) {
      console.error('Error deleting training category:', error);
      setError(error.response?.data?.detail || 'Failed to delete training category');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <BusinessIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Company Settings
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Basic Information" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    required
                    helperText="The primary name of your company"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Legal Name"
                    value={formData.legal_name}
                    onChange={handleInputChange('legal_name')}
                    helperText="Legal business name (if different)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Industry</InputLabel>
                    <Select
                      value={formData.industry}
                      onChange={handleInputChange('industry')}
                      label="Industry"
                    >
                      {INDUSTRY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Size</InputLabel>
                    <Select
                      value={formData.company_size}
                      onChange={handleInputChange('company_size')}
                      label="Company Size"
                    >
                      {SIZE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Description"
                    value={formData.description}
                    onChange={handleInputChange('description')}
                    multiline
                    rows={3}
                    helperText="Brief description of your company and business"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Contact Information" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={formData.address}
                    onChange={handleInputChange('address')}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.city}
                    onChange={handleInputChange('city')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={formData.state}
                    onChange={handleInputChange('state')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={formData.zip_code}
                    onChange={handleInputChange('zip_code')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={formData.country}
                    onChange={handleInputChange('country')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website}
                    onChange={handleInputChange('website')}
                    helperText="Include https://"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Compliance & Safety */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Compliance & Safety Information" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Primary Regulations"
                    value={formData.primary_regulations}
                    onChange={handleInputChange('primary_regulations')}
                    multiline
                    rows={3}
                    helperText="List primary regulations and standards (e.g., OSHA, FDA, etc.)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Safety Policies"
                    value={formData.safety_policies}
                    onChange={handleInputChange('safety_policies')}
                    multiline
                    rows={3}
                    helperText="Key safety policies and procedures"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Compliance Requirements"
                    value={formData.compliance_requirements}
                    onChange={handleInputChange('compliance_requirements')}
                    multiline
                    rows={3}
                    helperText="Specific compliance requirements for your industry"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* AI Training Context */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title="AI Training Context" 
              subheader="This information helps the AI generate more relevant training content"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Training Context"
                    value={formData.training_context}
                    onChange={handleInputChange('training_context')}
                    multiline
                    rows={4}
                    helperText="Additional context for AI training generation (company culture, specific needs, etc.)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Target Audience"
                    value={formData.target_audience}
                    onChange={handleInputChange('target_audience')}
                    multiline
                    rows={3}
                    helperText="Primary training audience description (new employees, experienced workers, etc.)"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* System Settings */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="System Settings" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={formData.timezone}
                      onChange={handleInputChange('timezone')}
                      label="Timezone"
                    >
                      {TIMEZONE_OPTIONS_DEDUPED.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Departments Management */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <GroupsIcon />
                  <span>Departments</span>
                </Box>
              }
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleDepartmentDialogOpen()}
                >
                  Add Department
                </Button>
              }
            />
            <CardContent>
              {departmentsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : departments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No departments yet. Click "Add Department" to create one.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {departments.map((dept) => (
                        <TableRow key={dept.id}>
                          <TableCell>{dept.name}</TableCell>
                          <TableCell>{dept.description || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={dept.is_active ? 'Active' : 'Inactive'}
                              color={dept.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleDepartmentDialogOpen(dept)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDepartmentDelete(dept)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Locations Management */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationIcon />
                  <span>Locations</span>
                </Box>
              }
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleLocationDialogOpen()}
                >
                  Add Location
                </Button>
              }
            />
            <CardContent>
              {locationsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : locations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No locations yet. Click "Add Location" to create one.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locations.map((loc) => (
                        <TableRow key={loc.id}>
                          <TableCell>{loc.name}</TableCell>
                          <TableCell>{loc.address || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={loc.is_active ? 'Active' : 'Inactive'}
                              color={loc.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleLocationDialogOpen(loc)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleLocationDelete(loc)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Document Categories Management */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <DescriptionIcon />
                  <span>Document Categories</span>
                </Box>
              }
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleCategoryDialogOpen()}
                >
                  Add Category
                </Button>
              }
            />
            <CardContent>
              {categoriesLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : documentCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No document categories yet. Click "Add Category" to create one.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Color</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentCategories.map((cat) => (
                        <TableRow key={cat.id}>
                          <TableCell>{cat.name}</TableCell>
                          <TableCell>{cat.description || '-'}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '4px',
                                backgroundColor: cat.color || '#1976d2',
                                border: '1px solid #ccc',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cat.is_active ? 'Active' : 'Inactive'}
                              color={cat.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleCategoryDialogOpen(cat)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCategoryDelete(cat)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Training Categories Management */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <SchoolIcon />
                  <span>Training Categories</span>
                </Box>
              }
              action={
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleTrainingCategoryDialogOpen()}
                >
                  Add Category
                </Button>
              }
            />
            <CardContent>
              {trainingCategoriesLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : trainingCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No training categories yet. Click "Add Category" to create one.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Color</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trainingCategories.map((cat) => (
                        <TableRow key={cat.id}>
                          <TableCell>{cat.name}</TableCell>
                          <TableCell>{cat.description || '-'}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '4px',
                                backgroundColor: cat.color || '#1976d2',
                                border: '1px solid #ccc',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cat.is_active ? 'Active' : 'Inactive'}
                              color={cat.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleTrainingCategoryDialogOpen(cat)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleTrainingCategoryDelete(cat)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCompanySettings}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </form>

        {/* Department Dialog */}
        <Dialog open={departmentDialogOpen} onClose={handleDepartmentDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingDepartment ? 'Edit Department' : 'Add Department'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={departmentForm.description}
                  onChange={(e) => setDepartmentForm(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={departmentForm.is_active}
                      onChange={(e) => setDepartmentForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDepartmentDialogClose}>Cancel</Button>
            <Button onClick={handleDepartmentSave} variant="contained" disabled={!departmentForm.name}>
              {editingDepartment ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Location Dialog */}
        <Dialog open={locationDialogOpen} onClose={handleLocationDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={locationForm.address}
                  onChange={(e) => setLocationForm(prev => ({ ...prev, address: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={locationForm.is_active}
                      onChange={(e) => setLocationForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLocationDialogClose}>Cancel</Button>
            <Button onClick={handleLocationSave} variant="contained" disabled={!locationForm.name}>
              {editingLocation ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Category Dialog */}
        <Dialog open={categoryDialogOpen} onClose={handleCategoryDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingCategory ? 'Edit Document Category' : 'Add Document Category'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={categoryForm.icon}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
                  helperText="Material-UI icon name (e.g., 'description', 'folder')"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={categoryForm.is_active}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCategoryDialogClose}>Cancel</Button>
            <Button onClick={handleCategorySave} variant="contained" disabled={!categoryForm.name}>
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Training Category Dialog */}
        <Dialog open={trainingCategoryDialogOpen} onClose={handleTrainingCategoryDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editingTrainingCategory ? 'Edit Training Category' : 'Add Training Category'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={trainingCategoryForm.name}
                  onChange={(e) => setTrainingCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={trainingCategoryForm.description}
                  onChange={(e) => setTrainingCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Icon"
                  value={trainingCategoryForm.icon}
                  onChange={(e) => setTrainingCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
                  helperText="Material-UI icon name (e.g., 'school', 'book')"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={trainingCategoryForm.color}
                  onChange={(e) => setTrainingCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={trainingCategoryForm.is_active}
                      onChange={(e) => setTrainingCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTrainingCategoryDialogClose}>Cancel</Button>
            <Button onClick={handleTrainingCategorySave} variant="contained" disabled={!trainingCategoryForm.name}>
              {editingTrainingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default CompanySettings;
